import { FC } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";

import ProductImg1 from "images/Devi-Plastic/3-Heavy-Sink-Coupling.webp";
import ProductImg2 from "images/Devi-Plastic/3-LITE-MS-SS-SKREW.webp";
import ProductImg3 from "images/Devi-Plastic/4 -Heavy-with-Reducer.webp";
import ProductImg4 from "images/Devi-Plastic/4 -JUMBO.webp";
import ProductImg5 from "images/Devi-Plastic/4-HEAVY-ROYAL.webp";
import ProductImg6 from "images/Devi-Plastic/4-LITE-COUPLING.webp";

const dataofProducts = [
  {
    id: 1,
    slug: "3-inch-heavy-sink-coupling",
    name: "3 inch Heavy Sink Coupling",
    description:
      "Extra heavy sink coupling designed for strong grip and long-lasting drainage performance.",
    image: ProductImg1,
    sizes: ["3 inch"],
    status: "Best Seller",
  },
  {
    id: 2,
    slug: "3-inch-lite-ms-ss-screw",
    name: "3 inch Lite MS & SS Screw Coupling",
    description:
      "Lightweight sink coupling with MS & SS screws for easy installation and secure fitting.",
    image: ProductImg2,
    sizes: ["3 inch"],
    status: "New in",
  },
  {
    id: 3,
    slug: "4-inch-heavy-coupling-with-reducer",
    name: "4 inch Heavy Coupling with Reducer",
    description:
      "Heavy quality sink coupling with reducer, ideal for flexible pipe connections.",
    image: ProductImg3,
    sizes: ["4 inch"],
    status: "New in",
  },
  {
    id: 4,
    slug: "4-inch-jumbo-sink-coupling",
    name: "4 inch Jumbo Sink Coupling",
    description:
      "Jumbo size sink coupling suitable for high water flow and commercial usage.",
    image: ProductImg4,
    sizes: ["4 inch"],
    status: "Trending",
  },
  {
    id: 5,
    slug: "4-inch-heavy-royal-sink-coupling",
    name: "4 inch Heavy Royal Sink Coupling",
    description:
      "Premium royal finish heavy sink coupling with superior strength and elegant design.",
    image: ProductImg5,
    sizes: ["4 inch"],
    status: "Best Seller",
  },
  {
    id: 6,
    slug: "4-inch-lite-sink-coupling",
    name: "4 inch Lite Sink Coupling",
    description:
      "Lightweight 4-inch sink coupling for regular household drainage needs.",
    image: ProductImg6,
    sizes: ["4 inch"],
    status: "New in",
  },
];

const PageProductDetail: FC = () => {
  const { slug } = useParams();

  const product = dataofProducts.find((item) => item.slug === slug);

  if (!product) {
    return (
      <div className="container py-20 text-center">
        <h2 className="text-2xl font-semibold">Product not found</h2>
        <Link to="/products" className="text-primary-600 mt-4 inline-block">
          ← Back to products
        </Link>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      <Helmet>
        {/* ================= TITLE ================= */}
        <title>
          {product.name} | Sink Coupling Manufacturer | Devi Plastic Ahmedabad
        </title>

        {/* ================= META DESCRIPTION ================= */}
        <meta
          name="description"
          content={`${product.name} by Devi Plastic. ${product.description} Manufactured in Ahmedabad, Gujarat with premium quality plastic for long-lasting drainage performance.`}
        />

        {/* ================= KEYWORDS ================= */}
        <meta
          name="keywords"
          content={`${
            product.name
          }, sink coupling, plastic sink coupling, ${product.status?.toLowerCase()} sink coupling, kitchen sink coupling, bathroom sink waste coupling, drainage fittings manufacturer, Devi Plastic, Ahmedabad sink coupling`}
        />

        {/* ================= CANONICAL ================= */}
        <link
          rel="canonical"
          href={`https://www.deviplastic.in/product/${product.slug}`}
        />

        {/* ================= OPEN GRAPH ================= */}
        <meta property="og:title" content={`${product.name} | Devi Plastic`} />
        <meta
          property="og:description"
          content={`${product.description} Premium sink coupling manufactured by Devi Plastic.`}
        />
        <meta property="og:type" content="product" />
        <meta
          property="og:url"
          content={`https://www.deviplastic.in/product/${product.slug}`}
        />
        <meta
          property="og:image"
          content={`https://www.deviplastic.in${product.image}`}
        />

        {/* ================= TWITTER ================= */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.name} | Devi Plastic`} />
        <meta name="twitter:description" content={product.description} />

        {/* ================= PRODUCT STRUCTURED DATA ================= */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            image: [`https://www.deviplastic.in${product.image}`],
            description: product.description,
            sku: product.slug,
            brand: {
              "@type": "Brand",
              name: "Devi Plastic",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Devi Plastic",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Ahmedabad",
                addressRegion: "Gujarat",
                addressCountry: "IN",
              },
            },
            offers: {
              "@type": "Offer",
              url: `https://www.deviplastic.in/product/${product.slug}`,
              priceCurrency: "INR",
              availability: "https://schema.org/InStock",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Available Sizes",
                value: product.sizes.join(", "),
              },
              {
                "@type": "PropertyValue",
                name: "Product Status",
                value: product.status,
              },
            ],
          })}
        </script>
      </Helmet>

      <BgGlassmorphism />

      <div className="container relative py-20">
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          {/* LEFT: Image */}
          <div className="relative bg-white/80 dark:bg-neutral-900/70 rounded-3xl p-8 shadow-lg">
            {product.status && (
              <span className="absolute top-6 left-6 px-4 py-1 text-sm rounded-full bg-primary-500 text-white font-semibold">
                {product.status}
              </span>
            )}

            <img
              src={product.image}
              alt={product.name}
              className="w-full h-[380px] object-contain"
            />
          </div>

          {/* RIGHT: Content */}
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white">
              {product.name}
            </h1>

            <p className="mt-5 text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {product.description}
            </p>

            {/* Specs */}
            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-neutral-800 dark:text-white">
                  Available Size:
                </span>
                <span className="text-neutral-600 dark:text-neutral-400">
                  {product.sizes.join(", ")}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span className="font-semibold text-neutral-800 dark:text-white">
                  Material:
                </span>
                <span className="text-neutral-600 dark:text-neutral-400">
                  Premium Plastic
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span className="font-semibold text-neutral-800 dark:text-white">
                  Usage:
                </span>
                <span className="text-neutral-600 dark:text-neutral-400">
                  Kitchen & Wash Basin
                </span>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="tel:9924823363"
                className="inline-flex items-center justify-center rounded-xl bg-primary-500 px-8 py-3 text-white font-semibold hover:bg-primary-600 transition"
              >
                Call for Order
              </a>

              <Link
                to="/products"
                className="inline-flex items-center justify-center rounded-xl border border-neutral-300 dark:border-neutral-700 px-8 py-3 font-semibold text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition"
              >
                Back to Products
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageProductDetail;
